import axios from 'axios';
import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';
import { API_CONFIG } from '../utils/constants';
import { clearAccess } from '../utils/accessStore';

// Use API_URL from constants.js instead of hardcoding
const API_URL = API_CONFIG.BASE_URL;
const TOKEN_KEY = 'auth_token';
const REMEMBER_KEY = 'remember_me';
const USER_DATA_KEY = 'user_data';

// Configure axios instance
const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor to add token to requests
axiosInstance.interceptors.request.use(
  (config) => {
    const token = Cookies.get(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Calculate expiry time based on remember me
const getExpiryTime = (rememberMe) => {
  // 12 hours in days (for js-cookie format)
  const standardExpiry = 30;
  // 30 days if remember me is checked
  return rememberMe ? 30 : standardExpiry;
};

const authService = {
  login: async (username, password, rememberMe) => {
    try {
      const response = await axios.post(`${API_URL}${API_CONFIG.AUTH.LOGIN}`, {
        username,
        password,
      });
      
      const token = response.data;
      const expiryTime = getExpiryTime(rememberMe);
      
      // Store token in cookie
      Cookies.set(TOKEN_KEY, token, { expires: expiryTime, secure: true, sameSite: 'strict' });
      
      // Store remember me preference
      localStorage.setItem(REMEMBER_KEY, rememberMe ? 'true' : 'false');
      
      // Decode and store user data
      const decodedToken = jwtDecode(token);
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(decodedToken));
      
      return { success: true, data: decodedToken };
    } catch (error) {
      return {
        success: false,
        message: error.response?.data?.message || 'Login failed. Please try again.',
        status: error.response?.status,
      };
    }
  },

  logout: () => {
    clearAccess();
    Cookies.remove(TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
    window.location.href = '/';
  },

  isAuthenticated: () => {
    const token = Cookies.get(TOKEN_KEY);
    if (!token) return false;
    
    try {
      const decodedToken = jwtDecode(token);
      // Check if token is expired
      const currentTime = Date.now() / 1000;
      return decodedToken.exp > currentTime;
    } catch {
      return false;
    }
  },

  refreshTokenIfNeeded: () => {
    const token = Cookies.get(TOKEN_KEY);
    if (!token) return;

    try {
      const decodedToken = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      
      // If token will expire in the next hour, refresh it
      if (decodedToken.exp - currentTime < 3600) {
        // Extend token expiration
        const rememberMe = localStorage.getItem(REMEMBER_KEY) === 'true';
        Cookies.set(TOKEN_KEY, token, { 
          expires: getExpiryTime(rememberMe),
          secure: true,
          sameSite: 'strict'
        });
      }
    } catch {
      // If token can't be decoded, do nothing
    }
  },

  getUserData: () => {
    try {
      const userData = localStorage.getItem(USER_DATA_KEY);
      return userData ? JSON.parse(userData) : null;
    } catch {
      return null;
    }
  },
};

export default authService;
export { axiosInstance };
